import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const Mining = () => {
  useEffect(() => {
    document.title = 'Mining Services - VReco Energy | Bauxite & Limestone';

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const fadeElements = document.querySelectorAll('.fade-in');
    fadeElements.forEach(el => observer.observe(el));

    return () => {
      fadeElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="services-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Mining Services – Bauxite & Limestone</h1>
          <p>Responsible extraction, processing, and logistics built for cement and alumina value chains.</p>
        </div>
      </section>

      {/* Intro */}
      <section className="content-section">
        <div className="container">
          <div className="about-content mining-intro">
            <div className="about-text fade-in">
              <h2 className="section-title">Integrated Mineral Supply Chain Management</h2>
              <p style={{ textAlign: 'justify' }}>
                We deliver tightly controlled bauxite and limestone mining programs—covering resource modeling, mine
                planning, selective extraction, in-pit quality control, crushing/sizing, and end-to-end logistics—so
                your plants run with consistent feed and predictable costs.
              </p>
              <p style={{ textAlign: 'justify' }}>
                Our teams align production plans to DGMS, MoEF, and state PCB norms, with clear SOPs for safety,
                environmental stewardship, and contractor oversight.
              </p>
              <div style={{ marginTop: '1.5rem' }}>
                <Link to="/contact" className="cta-button">Talk to our mining team</Link>
              </div>
            </div>
            <div className="stats mining-stats fade-in">
              <div className="stat-item mining-stat-item">
                <span className="stat-label">Plan adherence</span>
                <span className="stat-number" style={{ fontSize: '2.2rem' }}>&gt;95%</span>
              </div>
              <div className="stat-item mining-stat-item">
                <span className="stat-label">Target safety baseline</span>
                <span className="stat-number" style={{ fontSize: '2.2rem' }}>Zero LTI</span>
              </div>
              <div className="stat-item mining-stat-item">
                <span className="stat-label">On-spec deliveries</span>
                <span className="stat-number" style={{ fontSize: '2.2rem' }}>CaO / Al<sub>2</sub>O<sub>3</sub></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="content-section">
        <div className="container">
          <h2 className="section-title fade-in">Core Capabilities</h2>
          <p className="section-subtitle fade-in">Selective mining with quality, safety, and compliance built in</p>

          <div className="services-grid">
            <div className="service-card fade-in">
              <div className="service-icon">🧭</div>
              <h3>Geology & Modeling</h3>
              <p>Geological mapping, drilling data ingestion, and block modeling tailored for bauxite and limestone deposits.</p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">📝</div>
              <h3>Planning & Permits</h3>
              <p>Mine sequencing, haul-road design, and statutory alignment with DGMS, MoEF, and state PCB requirements.</p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">⛏️</div>
              <h3>Selective Extraction</h3>
              <p>Overburden removal, bench control, grade control with sampling/on-site lab or portable XRF, and precise excavation.</p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">💥</div>
              <h3>Drilling & Blasting</h3>
              <p>Controlled blasting tuned for fragmentation, vibration limits, and nearby sensitive zones.</p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">⚙️</div>
              <h3>Crushing & Sizing</h3>
              <p>In-pit or fixed crushing/screening to customer specs with moisture control and stockpile management.</p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">🚚</div>
              <h3>Dispatch & Logistics</h3>
              <p>Weighbridge-integrated dispatch, covered transport when needed, and delivery tracking for predictable supply.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ore Focus */}
      <section className="content-section">
        <div className="container">
          <h2 className="section-title fade-in">Bauxite & Limestone Focus</h2>
          <p className="section-subtitle fade-in">On-spec feed for alumina refineries and cement/quicklime plants</p>

          <div className="services-grid">
            <div className="service-card fade-in">
              <div className="service-icon">🪨</div>
              <h3>Bauxite</h3>
              <p style={{ textAlign: 'justify' }}>
                High-recovery selective mining for metallurgical-grade bauxite with silica control, moisture management,
                and grade-wise stockpiles aligned to alumina refinery requirements.
              </p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">🏗️</div>
              <h3>Limestone</h3>
              <p style={{ textAlign: 'justify' }}>
                Consistent CaO/low MgO limestone for cement and quicklime, with blast-free/trim-blast options near
                sensitive zones and on-spec sizing for direct plant feed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Delivery Process */}
      <section className="section why-choose">
        <div className="container">
          <h2 className="section-title fade-in" style={{ color: 'white' }}>Delivery Process</h2>
          <p className="section-subtitle fade-in" style={{ color: 'rgba(255,255,255,0.9)' }}>Transparent, sequenced, and compliant execution</p>

          <div className="features-grid">
            <div className="feature-item fade-in">
              <div className="feature-icon">🛰️</div>
              <h3>1. Mapping & Modeling</h3>
              <p>Geological mapping, drilling data ingestion, and block modeling.</p>
            </div>
            <div className="feature-item fade-in">
              <div className="feature-icon">📍</div>
              <h3>2. Planning & Approvals</h3>
              <p>Mine sequencing, haul roads, drainage, and statutory clearances.</p>
            </div>
            <div className="feature-item fade-in">
              <div className="feature-icon">🛤️</div>
              <h3>3. Site Prep</h3>
              <p>Haul-road layout, drainage, overburden stripping, and bench preparation.</p>
            </div>
            <div className="feature-item fade-in">
              <div className="feature-icon">💥</div>
              <h3>4. Drilling & Blasting</h3>
              <p>Controlled blasting with vibration and dust controls.</p>
            </div>
            <div className="feature-item fade-in">
              <div className="feature-icon">⛏️</div>
              <h3>5. Selective Mining</h3>
              <p>Bench-by-bench extraction with sampling and in-pit quality checks.</p>
            </div>
            <div className="feature-item fade-in">
              <div className="feature-icon">⚙️</div>
              <h3>6. Crushing & QC</h3>
              <p>Crushing, screening, and on-site quality verification.</p>
            </div>
            <div className="feature-item fade-in">
              <div className="feature-icon">🚛</div>
              <h3>7. Dispatch</h3>
              <p>Stockpile management, moisture control, and weighbridge-integrated dispatch.</p>
            </div>
            <div className="feature-item fade-in">
              <div className="feature-icon">🌱</div>
              <h3>8. Reclamation</h3>
              <p>Progressive rehabilitation and environmental reporting.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Environment */}
      <section className="content-section">
        <div className="container">
          <h2 className="section-title fade-in">Safety & Environmental Stewardship</h2>
          <p className="section-subtitle fade-in">Zero-harm operations as a baseline target</p>

          <div className="services-grid">
            <div className="service-card fade-in">
              <div className="service-icon">🦺</div>
              <h3>Safety Management</h3>
              <p>SOPs, toolbox talks, PPE compliance, incident reporting, and contractor oversight across shifts.</p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">💧</div>
              <h3>Dust & Water Control</h3>
              <p>Water sprinkling, dust suppression on haul roads, drainage maintenance, and monitoring near sensitive receptors.</p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">🌳</div>
              <h3>Reclamation</h3>
              <p>Bench dressing, slope stabilization, and progressive green cover with clear documentation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment & Logistics */}
      <section className="content-section">
        <div className="container">
          <h2 className="section-title fade-in">Equipment & Logistics Readiness</h2>
          <p className="section-subtitle fade-in">Right fleet for excavation, haulage, and processing</p>

          <div className="services-grid">
            <div className="service-card fade-in">
              <div className="service-icon">🏗️</div>
              <h3>Earthmoving & Haulage</h3>
              <p>Excavators, tippers/dumpers, dozers, motor graders, and water tankers matched to production plans.</p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">🛠️</div>
              <h3>Drilling & Blasting</h3>
              <p>Production drills, controlled blasting tools, and vibration monitoring for compliant operations.</p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">📏</div>
              <h3>Crushing & Screening</h3>
              <p>Primary/secondary crushers and screens for on-spec sizing, integrated with weighbridge dispatch.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section why-choose">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '650px', margin: '0 auto' }}>
            <h2 style={{ color: 'white', marginBottom: '1rem' }}>Looking for on-spec bauxite or limestone supply?</h2>
            <p style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '2rem', fontSize: '1.05rem' }}>
              Share your deposit data, target specs, and delivery timelines—we will align a compliant mining and dispatch plan.
            </p>
            <Link to="/contact" className="cta-button" style={{ fontSize: '1.1rem', padding: '1.1rem 3rem' }}>
              Let&apos;s Plan Your Supply
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mining;

